import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { toastr } from "react-redux-toastr";
import { userService } from "@/features/profile/services/userService";
import { HTTP_STATUS, userConstants, authConstants } from "@/config/constants";
import { getUserRole } from "@/config/appConfig";
import MESSAGE from "@/helpers/messages";

type UserRecord = Record<string, any>;

type UserState = {
  error: unknown;
  isUserUpdated: boolean;
  loading: boolean;
  role: string;
  user: UserRecord;
};

type RootState = {
  user: UserState;
};

export const getUserThunk = createAsyncThunk(
  "user/getUser",
  async (payload: UserRecord | undefined, { rejectWithValue }) => {
    try {
      const res = await userService.getUser(payload);
      if (res.status === HTTP_STATUS.OK && res.data) {
        let userData: UserRecord = {};
        if (res.data.rows[0]) {
          userData = { ...res.data.rows[0] };
          if (userData.id) delete userData.id;
        }
        return userData;
      }
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const updateUserThunk = createAsyncThunk(
  "user/updateUser",
  async (payload: UserRecord, { dispatch, rejectWithValue }) => {
    // eslint-disable-next-line no-unused-vars
    const { brandId, ...rest } = payload;
    try {
      const res = await userService.updateUser(rest);
      if (res.status === HTTP_STATUS.OK) {
        await dispatch(getUserThunk(payload));
        toastr.success("Success", MESSAGE.updateSuccess("User"));
        return res.data;
      }
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

const initialState: UserState = {
  role: "",
  user: {},
  error: null,
  loading: false,
  isUserUpdated: false,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<UserRecord>) {
      const payload = action.payload;
      const brandData = (payload.brandData || state.user.brandData || []).map(
        (b: UserRecord) => {
          if (b.fk_user_id === (payload.id || state.user.id)) {
            return {
              ...b,
              brand_logo: payload.logo_name || state.user.logo_name,
            };
          }
          return b;
        }
      );
      state.user = { ...state.user, ...payload, brandData };
      state.role = getUserRole(payload.fk_role_id);
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isUserUpdated = false;
      })
      .addCase(getUserThunk.fulfilled, (state, action) => {
        const payload = action.payload;
        if (!payload) return;
        const brandData = (payload.brandData || state.user.brandData || []).map(
          (b: UserRecord) => {
            if (b.fk_user_id === (payload.id || state.user.id)) {
              return {
                ...b,
                brand_logo: payload.logo_name || state.user.logo_name,
              };
            }
            return b;
          }
        );
        state.user = { ...state.user, ...payload, brandData };
        state.role = getUserRole(payload.fk_role_id);
        state.loading = false;
        state.error = null;
      })
      .addCase(getUserThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUserThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isUserUpdated = false;
      })
      .addCase(updateUserThunk.fulfilled, (state) => {
        state.loading = false;
        state.isUserUpdated = true;
      })
      .addCase(updateUserThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Legacy action dispatched by authSaga after login
      .addCase(
        userConstants.GET_USER_SUCCESS,
        (state, action: PayloadAction<UserRecord | undefined>) => {
          const payload = action.payload;
          if (!payload) return;
          state.user = { ...state.user, ...payload };
          state.role = getUserRole(payload.fk_role_id);
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(authConstants.LOGOUT, () => initialState);
  },
});

export const { setUser } = userSlice.actions;
export default userSlice.reducer;

// Selectors
export const userSelector = (state: RootState) => state.user.user;
export const isLoadingSelector = (state: RootState) => state.user.loading;
export const isErrorSelector = (state: RootState) => state.user.error;
export const isUserUpdatedSelector = (state: RootState) =>
  state.user.isUserUpdated;
export const loggedInUserSelector = (state: RootState) => state.user.user;
export const loggedInUserRoleSelector = (state: RootState) => state.user.role;
